import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { PLANBASE_CONFIG } from '../config';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// Taller input for better touch targets on mobile
function MobileInput({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={[
        'flex w-full rounded-lg border border-input bg-background px-3 text-sm ring-offset-background',
        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'placeholder:text-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'h-12 md:h-9',
        className,
      ].join(' ')}
    />
  );
}

// Hide Google login on iOS native — Apple guidelines require Sign in with Apple
// if any third-party social login is offered, and we're not adding that yet.
const isIOS = Capacitor.getPlatform() === 'ios';
const showGoogleButton = !isIOS;

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail,
          project_id: PLANBASE_CONFIG.projectId,
          redirect_url: `${window.location.origin}/auth/reset-password`,
          project_name: PLANBASE_CONFIG.project.name,
          project_logo: PLANBASE_CONFIG.project.logo_url,
          primary_color: PLANBASE_CONFIG.project.primary_color,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setForgotMessage('Password reset email sent. Please check your inbox.');
      } else {
        setForgotMessage(data.error || 'Failed to send reset email. Please try again.');
      }
    } catch {
      setForgotMessage('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err: any) {
      toast.error(err.message || 'Incorrect email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      navigate('/');
    } catch (err: any) {
      toast.error(err.message || 'Google login failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6">
      <div className="w-full max-w-sm">
        <div className="bg-card border border-border rounded-3xl overflow-hidden">

          {/* Top section — shown with Google button, or as plain header on iOS */}
          <div className={`px-6 pt-6 pb-5 border-b border-border rounded-b-3xl ${showGoogleButton ? 'bg-[#f0f0f0] dark:bg-muted/50' : ''}`}>
            <h1 className="text-xl font-semibold text-foreground mb-0.5">
              {forgotPassword ? 'Reset password' : 'Login'}
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
              {forgotPassword ? 'Enter your email to receive a reset link' : (
                <>Don't have an account?{' '}
                  <Link to="/signup" className="text-foreground underline font-medium">Sign up</Link>
                </>
              )}
            </p>

            {showGoogleButton && !forgotPassword && (
              <Button
                type="button"
                variant="outline"
                mobileFullHeight
                className="w-full gap-2"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                Login with Google
              </Button>
            )}
          </div>

          {/* Form section */}
          <div className="px-6 py-5">
            {forgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <MobileInput
                    type="email"
                    placeholder="your@email.com"
                    autoComplete="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>

                {forgotMessage && (
                  <p className={`text-xs ${forgotMessage.includes('sent') ? 'text-green-600' : 'text-red-500'}`}>
                    {forgotMessage}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={forgotLoading}
                  mobileFullHeight
                  className="w-full"
                >
                  {forgotLoading ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Sending...</> : 'Send Reset Email'}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => { setForgotPassword(false); setForgotMessage(''); }}
                    className="text-primary hover:text-primary font-medium"
                  >
                    Back to Sign In
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <MobileInput
                    type="email"
                    placeholder="your@email.com"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">Password</label>
                    <button
                      type="button"
                      onClick={() => { setForgotPassword(true); setForgotEmail(formData.email); }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <MobileInput
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      // Padding-right to avoid text going behind the eye icon
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  mobileFullHeight
                  className="w-full"
                >
                  {loading ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Signing in...</> : 'Login'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
